import { normalizeBlockedActions } from '../modules/warehouse/blockedActions';
import type {
  OrderProductRealizationDto,
  OrderTransportsDto,
  RouteOrderDto,
  SupplierOrderTransportDto,
  SupplierOrderTruckFreightDto,
  TransportCargoItemDto,
  TransportOrderSummaryDto,
  TransportProductDto,
  TransportFreightExchangeRateDto,
  TransportInvoiceDto,
  TransportRouteDetailDto,
  TransportRouteStatus,
  TransportRouteSummaryDto,
  TransportSupplierCargoContextDto,
  TransportSupplierCargoWarningDto,
  TransportSupplierOrderCarriedDto,
  TransportSupplierOrderFreightShareDto,
  TransportSupplierOrderLoadItemDto,
  TransportSupplierOrderSummaryDto,
} from '../types/transport';

import {
  asArray,
  asBoolean,
  asNumber,
  asOptionalArray,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from './common';
import { normalizeCustomer, normalizeUser } from './order';
import { getDisplayOrderNumber } from '../types/order';
import { normalizeTransportAddressList } from '../utils/transportAddressUtils';
import {
  normalizeOrderDeliveryMode,
  normalizeTransportDirection,
  normalizeTransportKind,
} from '../modules/transports/transportDirection';
import {
  TRANSPORT_COST_ALLOCATION_METHODS,
  type AllocationKeyAvailability,
  type AllocationMethod as TransportCostAllocationMethod,
  type TransportCostAllocationResult,
  type TransportCostAllocationWarningCode,
} from '../utils/transportCostAllocation';

export type {
  OrderProductRealizationDto,
  OrderTransportsDto,
  RouteOrderDto,
  SupplierOrderTransportDto,
  TransportCargoItemDto,
  TransportOrderSummaryDto,
  TransportProductDto,
  TransportRouteDetailDto,
  TransportRouteStatus,
  TransportRouteSummaryDto,
  TransportSupplierOrderSummaryDto,
} from '../types/transport';

const ROUTE_STATUSES: TransportRouteStatus[] = [
  'PENDING',
  'SCHEDULED',
  'LOADING',
  'IN_TRANSIT',
  'DELIVERED',
  'CANCELLED',
  'PLANNED',
  'DELAYED',
];

export function normalizeTransportRouteSummary(
  raw: unknown,
  entity = 'transportRoute',
): TransportRouteSummaryDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  const routeOrders = normalizeRouteOrders(record?.routeOrders, `${entity}.routeOrders`);
  const fallbackOrder = routeOrders.length === 0
    ? normalizeRouteOrderFallback(record, `${entity}.fallbackOrder`)
    : null;
  const effectiveRouteOrders = routeOrders.length > 0 ? routeOrders : fallbackOrder ? [fallbackOrder] : [];
  const firstOrder = effectiveRouteOrders[0]?.order;
  const products = normalizeTransportProducts(record?.products, `${entity}.products`);

  return {
    warehouseDocuments: asOptionalArray(record?.warehouseDocuments, value => value)?.flatMap((raw) => {
      const document = asRecord(raw);
      if (!document || !document.id || !['pz', 'wz'].includes(asString(document.kind))) return [];
      return [{ id: asString(document.id), kind: document.kind as 'pz' | 'wz',
        number: asString(document.number), status: asString(document.status),
        orderId: asOptionalString(document.orderId), orderNumber: asOptionalString(document.orderNumber) }];
    }),
    id: asString(record?.id),
    routeNumber: asString(record?.routeNumber),
    displayLabel: asOptionalString(record?.displayLabel) ?? null,
    originCity: asString(record?.originCity),
    originCountry: asString(record?.originCountry),
    destinationCity: asString(record?.destinationCity),
    destinationCountry: asString(record?.destinationCountry),
    departureDate: asString(record?.departureDate),
    estimatedArrival: asString(record?.estimatedArrival),
    actualArrival: asOptionalString(record?.actualArrival),
    transportCompany: asString(record?.transportCompany),
    transportCompanyId: asOptionalString(record?.transportCompanyId) ?? null,
    transportContact: asOptionalString(record?.transportContact),
    vehicleRegistration: asOptionalString(record?.vehicleRegistration),
    driverName: asOptionalString(record?.driverName),
    driverPhone: asOptionalString(record?.driverPhone),
    transportCost: asNumber(record?.transportCost),
    transportRate: record?.transportRate == null ? undefined : asNumber(record?.transportRate),
    currency: asString(record?.currency, 'PLN'),
    costAllocationMethod: normalizeTransportCostAllocationMethod(record?.costAllocationMethod),
    // Detail only; absent (older response, list) = the form uses its mirror.
    ...(asRecord(record?.allocationKeyAvailability)
      ? { allocationKeyAvailability: normalizeAllocationKeyAvailability(record?.allocationKeyAvailability) }
      : {}),
    direction: normalizeTransportDirection(record?.direction),
    costAllocation: normalizeTransportCostAllocation(record?.costAllocation, `${entity}.costAllocation`),
    invoiceNumber: asOptionalString(record?.invoiceNumber),
    status: normalizeTransportStatus(record?.status, `${entity}.status`),
    // Backend-computed "why is this unavailable" (never re-derived in pages).
    blockedActions: normalizeBlockedActions(record?.blockedActions),
    notes: asOptionalString(record?.notes),
    orderId: asOptionalString(record?.orderId) || effectiveRouteOrders[0]?.orderId,
    orderNumber: asOptionalString(record?.orderNumber) || firstOrder?.orderNumber || '',
    displayOrderNumber:
      asOptionalString(record?.displayOrderNumber)
      || firstOrder?.displayOrderNumber
      || asOptionalString(record?.orderNumber)
      || firstOrder?.orderNumber
      || '',
    customerName: pickTransportCustomerName(record, firstOrder),
    customer: asOptionalString(record?.customer),
    products: products.length > 0 ? products : firstOrder?.products || [],
    routeOrders: effectiveRouteOrders,
    // Etap 2: absent on a pre-migration-128 backend (and on any transport with
    // no inbound leg) — defaults to [] rather than reporting an anomaly, so an
    // older response degrades to "no supplier orders" instead of noise.
    supplierOrders: normalizeTransportSupplierOrders(
      record?.supplierOrders,
      `${entity}.supplierOrders`,
    ),
    cargoItems: normalizeTransportCargoItems(record?.cargoItems, `${entity}.cargoItems`),
    loadingDate: asOptionalString(record?.loadingDate),
    estimatedDelivery: asOptionalString(record?.estimatedDelivery),
    actualDelivery: asOptionalString(record?.actualDelivery),
    loadingAddresses: normalizeTransportAddressList(record?.loadingAddresses),
    deliveryAddresses: normalizeTransportAddressList(record?.deliveryAddresses),
    dimensions: asOptionalString(record?.dimensions),
    licensePlate1: asOptionalString(record?.licensePlate1),
    licensePlate2: asOptionalString(record?.licensePlate2),
    totalWeight: record?.totalWeight == null ? undefined : asNumber(record?.totalWeight),
    totalVolume: record?.totalVolume == null ? undefined : asNumber(record?.totalVolume),
    createdAt: asString(record?.createdAt),
    updatedAt: asString(record?.updatedAt),
  };
}

export function normalizeTransportRoutes(
  raw: unknown,
  entity = 'transportRoutes',
): TransportRouteSummaryDto[] {
  return asArray(raw, (item, index) => normalizeTransportRouteSummary(item, `${entity}[${index}]`));
}

export function normalizeTransportRouteDetail(
  raw: unknown,
  entity = 'transportRouteDetail',
): TransportRouteDetailDto {
  const summary = normalizeTransportRouteSummary(raw, entity);
  const record = asRecord(raw);
  const createdBy = normalizeUser(record?.createdBy, `${entity}.createdBy`);

  return {
    ...summary,
    createdByName: createdBy?.name || '',
    createdByEmail: createdBy?.email || '',
    invoice: normalizeTransportInvoice(record),
    freightExchangeRate: normalizeFreightExchangeRate(record?.freightExchangeRate),
  };
}

function normalizeFreightExchangeRate(raw: unknown): TransportFreightExchangeRateDto | null {
  const record = asRecord(raw);
  const rate = Number(record?.rate);
  if (!record || !Number.isFinite(rate) || rate <= 0) return null;
  return {
    rate,
    tableNumber: asOptionalString(record.tableNumber) ?? null,
    effectiveDate: asOptionalString(record.effectiveDate)?.slice(0, 10) ?? null,
  };
}

function normalizeTransportInvoice(record: Record<string, unknown> | null): TransportInvoiceDto | null {
  if (record?.invoiceAmount == null || record?.invoiceAmount === '') return null;
  return {
    number: asString(record.invoiceNumber),
    issueDate: asString(record.invoiceIssueDate).slice(0, 10),
    amount: asNumber(record.invoiceAmount),
    currency: asString(record.invoiceCurrency, 'PLN'),
    plannedRate: record.plannedTransportRate == null ? null : asNumber(record.plannedTransportRate),
    plannedCurrency: asOptionalString(record.plannedCurrency) ?? null,
  };
}

export function normalizeOrderTransports(
  raw: unknown,
  entity = 'orderTransports',
): OrderTransportsDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    transports: asArray(record?.transports, (item, index) =>
      normalizeTransportRouteDetail(item, `${entity}.transports[${index}]`),
    ),
    productRealization: asArray(record?.productRealization, (item, index) =>
      normalizeOrderProductRealization(item, `${entity}.productRealization[${index}]`),
    ),
  };
}

function normalizeOrderProductRealization(
  raw: unknown,
  entity: string,
): OrderProductRealizationDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    orderProductId: asString(record?.orderProductId),
    name: asString(record?.name),
    unit: asOptionalString(record?.unit) ?? null,
    commercialUnit: asOptionalString(record?.commercialUnit) ?? null,
    orderedCommercialQuantity: record?.orderedCommercialQuantity == null ? null : asNumber(record.orderedCommercialQuantity),
    allocatedCommercialQuantity: record?.allocatedCommercialQuantity == null ? null : asNumber(record.allocatedCommercialQuantity),
    remainingCommercialQuantity: record?.remainingCommercialQuantity == null ? null : asNumber(record.remainingCommercialQuantity),
    orderedQuantity: asNumber(record?.orderedQuantity),
    allocatedQuantity: asNumber(record?.allocatedQuantity),
    remainingQuantity: asNumber(record?.remainingQuantity),
    percent: asNumber(record?.percent),
    deliveredQuantity: asNumber(record?.deliveredQuantity),
    deliveryPercent: asNumber(record?.deliveryPercent),
  };
}

function normalizeRouteOrders(raw: unknown, entity: string): RouteOrderDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);
    const order = normalizeTransportOrderSummary(record?.order, `${entity}[${index}].order`);

    return {
      id: asString(record?.id) || asString(record?.orderId) || order.id,
      orderId: asString(record?.orderId) || order.id,
      loadingAddress: asOptionalString(record?.loadingAddress) ?? null,
      deliveryAddress: asOptionalString(record?.deliveryAddress) ?? null,
      allocationMethod: asOptionalString(record?.allocationMethod) ?? undefined,
      allocationBasisValue: record?.allocationBasisValue == null ? undefined : asNumber(record?.allocationBasisValue),
      allocationPercent: record?.allocationPercent == null ? undefined : asNumber(record?.allocationPercent),
      allocatedTransportCost: record?.allocatedTransportCost == null ? undefined : asNumber(record?.allocatedTransportCost),
      // P8: null/undefined means "no conversion happened" (plain-PLN
      // transport) — never coerce to a guessed currency.
      originalCurrency: asOptionalString(record?.originalCurrency) ?? null,
      originalAmount: record?.originalAmount == null ? null : asNumber(record?.originalAmount),
      exchangeRateUsed: record?.exchangeRateUsed == null ? null : asNumber(record?.exchangeRateUsed),
      exchangeRateSource: asOptionalString(record?.exchangeRateSource) ?? null,
      exchangeRateTableNumber: asOptionalString(record?.exchangeRateTableNumber) ?? null,
      exchangeRateRequestedDate: asOptionalString(record?.exchangeRateRequestedDate) ?? null,
      exchangeRateEffectiveDate: asOptionalString(record?.exchangeRateEffectiveDate) ?? null,
      exchangeRateIsFallback: record?.exchangeRateIsFallback == null ? null : asBoolean(record.exchangeRateIsFallback),
      cargoDescription: asOptionalString(record?.cargoDescription),
      weight: record?.weight == null ? undefined : asNumber(record?.weight),
      volume: record?.volume == null ? undefined : asNumber(record?.volume),
      areaSqm: record?.areaSqm == null ? undefined : asNumber(record?.areaSqm),
      ...(asRecord(record?.totalQuantityByUnit)
        ? { totalQuantityByUnit: normalizeQuantityByUnit(record?.totalQuantityByUnit) }
        : {}),
      productRealization: asArray(record?.productRealization, (item, realizationIndex) =>
        normalizeOrderProductRealization(
          item,
          `${entity}[${index}].productRealization[${realizationIndex}]`,
        )
      ),
      order,
    };
  });
}

function normalizeRouteOrderFallback(record: ReturnType<typeof asRecord>, entity: string): RouteOrderDto | null {
  if (!record || !record.orderId) {
    return null;
  }

  return {
    id: asString(record.orderId),
    orderId: asString(record.orderId),
    productRealization: [],
    order: normalizeTransportOrderSummary(record, `${entity}.order`),
  };
}

function normalizeTransportOrderSummary(raw: unknown, entity: string): TransportOrderSummaryDto {
  const record = asRecord(raw);
  const customer = normalizeCustomer(record?.customer, `${entity}.customer`);
  const orderNumber = asString(record?.orderNumber);
  const manualOrderNumber = asOptionalString(record?.manualOrderNumber) ?? null;
  const useManualNumbering = record?.useManualNumbering === true;

  return {
    id: asString(record?.id),
    orderNumber,
    manualOrderNumber,
    useManualNumbering,
    displayOrderNumber:
      asOptionalString(record?.displayOrderNumber)
      || getDisplayOrderNumber({ orderNumber, manualOrderNumber, useManualNumbering }),
    factoryLocation: asOptionalString(record?.factoryLocation) ?? null,
    deliveryLocation: asOptionalString(record?.deliveryLocation) ?? null,
    clientOrderNumber: asOptionalString(record?.clientOrderNumber) ?? null,
    customerName: customer?.name || asOptionalString(record?.customerName) || '',
    revenue: asNumber(record?.revenue),
    currency: asString(record?.currency, 'PLN'),
    status: asString(record?.status),
    products: normalizeTransportProducts(record?.products, `${entity}.products`),
    customer,
    ...(record?.deliveryMode == null ? {} : { deliveryMode: normalizeOrderDeliveryMode(record.deliveryMode) }),
  };
}

/**
 * Etap 2 (transport przywozowy): supplier orders (ZD) linked to a transport.
 * The backend already resolves the canonical display number
 * (buildSupplierOrderDisplayNumberSql), so this only falls back to the raw
 * `orderNumber` when an older response omits it.
 */
export function normalizeTransportSupplierOrders(
  raw: unknown,
  entity = 'transportSupplierOrders',
): TransportSupplierOrderSummaryDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    const orderNumber = asString(record?.orderNumber);

    const summary: TransportSupplierOrderSummaryDto = {
      id: asString(record?.id),
      orderNumber,
      displayOrderNumber: asOptionalString(record?.displayOrderNumber) || orderNumber,
      supplierName: asOptionalString(record?.supplierName) ?? null,
    };
    if (record && 'draftSourceOrderId' in record) summary.draftSourceOrderId = asOptionalString(record.draftSourceOrderId) ?? null;
    if (record && 'draftReviewStatus' in record) summary.draftReviewStatus = asOptionalString(record.draftReviewStatus) ?? null;
    // Detail-only extras: keep them off the object entirely when the response
    // does not carry them, so list shapes stay exactly as before.
    if (Array.isArray(record?.loadItems)) {
      summary.loadItems = record.loadItems.map(normalizeSupplierOrderLoadItem);
    }
    if (record && 'freightShare' in record) {
      summary.freightShare = normalizeSupplierOrderFreightShare(record.freightShare);
    }
    if (Array.isArray(record?.cargoWarnings)) {
      summary.cargoWarnings = record.cargoWarnings
        .map(normalizeSupplierCargoWarning)
        .filter((warning: TransportSupplierCargoWarningDto) => Boolean(warning.code));
    }
    if (record && 'cargoContext' in record) {
      summary.cargoContext = normalizeSupplierCargoContext(record.cargoContext);
    }
    if (Array.isArray(record?.receiptDocuments)) {
      summary.receiptDocuments = record.receiptDocuments.map((document: unknown) => {
        const documentRecord = asRecord(document);
        return {
          id: asString(documentRecord?.id),
          pzNumber: asString(documentRecord?.pzNumber),
          sharedWithOtherTransports: documentRecord?.sharedWithOtherTransports === true,
        };
      });
    }
    return summary;
  }).filter((supplierOrder) => Boolean(supplierOrder.id));
}

// pg NUMERIC and JSON may deliver numbers as strings; missing stays null.
const asNullableNumber = (value: unknown): number | null => (
  value === null || value === undefined || value === '' ? null : asNumber(value, Number.NaN)
);
const finiteOrNull = (value: number | null): number | null => (
  value !== null && Number.isFinite(value) ? value : null
);

function normalizeSupplierOrderLoadItem(item: unknown): TransportSupplierOrderLoadItemDto {
  const record = asRecord(item);
  return {
    id: asString(record?.id),
    productVariantId: asOptionalString(record?.productVariantId) ?? null,
    name: asString(record?.name),
    ...(record?.lineNumber == null ? {} : { lineNumber: asNumber(record.lineNumber) }),
    quantity: finiteOrNull(asNullableNumber(record?.quantity)),
    unit: asOptionalString(record?.unit) ?? null,
    width: finiteOrNull(asNullableNumber(record?.width)),
    height: finiteOrNull(asNullableNumber(record?.height)),
    length: finiteOrNull(asNullableNumber(record?.length)),
    thickness: finiteOrNull(asNullableNumber(record?.thickness)),
    dimensionDisplayProfile: asOptionalString(record?.dimensionDisplayProfile) ?? null,
    quantityPcs: finiteOrNull(asNullableNumber(record?.quantityPcs)),
    quantityM2: finiteOrNull(asNullableNumber(record?.quantityM2)),
    quantityM3: finiteOrNull(asNullableNumber(record?.quantityM3)),
    ...(record?.countInPiecesOnly === true ? { countInPiecesOnly: true } : {}),
    pieceWeightKg: finiteOrNull(asNullableNumber(record?.pieceWeightKg)),
    ...(record && 'freightPLN' in record
      ? { freightPLN: finiteOrNull(asNullableNumber(record.freightPLN)) }
      : {}),
    ...(asRecord(record?.received)
      ? {
        received: {
          pcs: finiteOrNull(asNullableNumber(asRecord(record?.received)?.pcs)),
          m2: finiteOrNull(asNullableNumber(asRecord(record?.received)?.m2)),
          m3: finiteOrNull(asNullableNumber(asRecord(record?.received)?.m3)),
        },
      }
      : {}),
    // Addendum A: absent keys stay absent (older response); null stays null.
    ...(record && 'carried' in record ? { carried: normalizeCarried(record.carried) } : {}),
    ...(record && 'entered' in record ? { entered: normalizeCarried(record.entered) } : {}),
    ...(record && 'onOtherTrucks' in record ? { onOtherTrucks: normalizeCarried(record.onOtherTrucks) } : {}),
    ...(record && 'remaining' in record ? { remaining: normalizeCarried(record.remaining) } : {}),
    ...(record && 'carriedSource' in record
      ? { carriedSource: normalizeCarriedSource(record.carriedSource) }
      : {}),
  };
}

const CARRIED_SOURCES = ['EXPLICIT', 'REMAINDER', 'AMBIGUOUS', 'NONE'] as const;

function normalizeCarriedSource(value: unknown): TransportSupplierOrderLoadItemDto['carriedSource'] {
  return (CARRIED_SOURCES as readonly string[]).includes(String(value))
    ? value as (typeof CARRIED_SOURCES)[number]
    : null;
}

function normalizeCarried(raw: unknown): TransportSupplierOrderCarriedDto | null {
  const record = asRecord(raw);
  if (!record) return null;
  return {
    quantity: finiteOrNull(asNullableNumber(record.quantity)),
    unit: asOptionalString(record.unit) ?? null,
    quantityPcs: finiteOrNull(asNullableNumber(record.quantityPcs)),
    quantityM2: finiteOrNull(asNullableNumber(record.quantityM2)),
    quantityM3: finiteOrNull(asNullableNumber(record.quantityM3)),
  };
}

/** `cargoContext` of a ZD (contract round 8); `null` for a missing / malformed one. */
export function normalizeSupplierCargoContext(raw: unknown): TransportSupplierCargoContextDto | null {
  const record = asRecord(raw);
  if (!record) return null;
  const explicit = asRecord(record.otherExplicitByItemId) || {};
  return {
    otherTrucks: (Array.isArray(record.otherTrucks) ? record.otherTrucks : [])
      .map((truck: unknown) => {
        const truckRecord = asRecord(truck);
        return {
          id: asString(truckRecord?.id),
          label: asOptionalString(truckRecord?.label) || asString(truckRecord?.id),
          status: asOptionalString(truckRecord?.status) ?? null,
          listed: truckRecord?.listed === true,
        };
      })
      .filter((truck: { id: string }) => Boolean(truck.id)),
    otherExplicitByItemId: Object.fromEntries(Object.entries(explicit)
      .map(([itemId, value]) => [itemId, normalizeCarried(value)] as const)
      .filter((entry): entry is [string, TransportSupplierOrderCarriedDto] => entry[1] !== null)),
    ...(asRecord(record.pieceWeightKgByItemId) ? {
      pieceWeightKgByItemId: Object.fromEntries(Object.entries(asRecord(record.pieceWeightKgByItemId) || {})
        .map(([itemId, value]) => [itemId, finiteOrNull(asNullableNumber(value))] as const)
        .filter((entry): entry is [string, number] => entry[1] !== null && entry[1] > 0)),
    } : {}),
  };
}

/** `cargoWarnings` of a ZD (transport detail, ZD valuation); entries without a code are dropped. */
export function normalizeSupplierCargoWarnings(raw: unknown): TransportSupplierCargoWarningDto[] {
  return (Array.isArray(raw) ? raw : [])
    .map(normalizeSupplierCargoWarning)
    .filter((warning) => Boolean(warning.code));
}

function normalizeSupplierCargoWarning(raw: unknown): TransportSupplierCargoWarningDto {
  const record = asRecord(raw);
  const strings = (value: unknown) => (Array.isArray(value) ? value.map((entry) => String(entry)).filter(Boolean) : undefined);
  return {
    code: asString(record?.code),
    ...(strings(record?.transportIds) ? { transportIds: strings(record?.transportIds) } : {}),
    ...(strings(record?.transportLabels) ? { transportLabels: strings(record?.transportLabels) } : {}),
    ...(strings(record?.supplierOrderItemIds) ? { supplierOrderItemIds: strings(record?.supplierOrderItemIds) } : {}),
    ...(record?.supplierOrderItemId ? { supplierOrderItemId: asString(record.supplierOrderItemId) } : {}),
    ...(record?.excess == null ? {} : { excess: asNumber(record.excess) }),
    ...(record && 'unit' in record ? { unit: asOptionalString(record.unit) ?? null } : {}),
    ...(record?.supplierOrderId ? { supplierOrderId: asString(record.supplierOrderId) } : {}),
    ...(Array.isArray(record?.remaining) ? {
      remaining: record.remaining.flatMap((entry: unknown) => {
        const entryRecord = asRecord(entry);
        const carried = normalizeCarried(entry);
        if (!entryRecord || !carried || !entryRecord.supplierOrderItemId) return [];
        return [{
          ...carried,
          supplierOrderItemId: asString(entryRecord.supplierOrderItemId),
          productName: asOptionalString(entryRecord.productName) ?? null,
          ...(entryRecord.position == null ? {} : { position: asNumber(entryRecord.position) }),
        }];
      }),
    } : {}),
  };
}

function normalizeQuantityByUnit(raw: unknown): Partial<Record<'pcs' | 'm2' | 'm3', number>> {
  const record = asRecord(raw) || {};
  const result: Partial<Record<'pcs' | 'm2' | 'm3', number>> = {};
  (['pcs', 'm2', 'm3'] as const).forEach((unit) => {
    const value = finiteOrNull(asNullableNumber(record[unit]));
    if (value !== null) result[unit] = value;
  });
  return result;
}

function normalizeAllocationKeyAvailability(raw: unknown): AllocationKeyAvailability {
  const record = asRecord(raw) || {};
  return Object.fromEntries(TRANSPORT_COST_ALLOCATION_METHODS.map((key) => {
    const entry = asRecord(record[key]);
    const reasons = Array.isArray(entry?.reasons)
      ? entry!.reasons.flatMap((reason: unknown) => {
        const reasonRecord = asRecord(reason);
        const text = asOptionalString(reasonRecord?.reason);
        return text ? [{ productName: asOptionalString(reasonRecord?.productName) ?? null, reason: text }] : [];
      })
      : [];
    // A key the response does not describe is not greyed out (unknown ≠ unavailable).
    return [key, { available: entry ? entry.available !== false : true, reasons }];
  })) as AllocationKeyAvailability;
}

function normalizeSupplierOrderFreightShare(raw: unknown): TransportSupplierOrderFreightShareDto | null {
  const record = asRecord(raw);
  if (!record) return null;
  return {
    percent: finiteOrNull(asNullableNumber(record.percent)),
    originalAmount: finiteOrNull(asNullableNumber(record.originalAmount)),
    currency: asOptionalString(record.currency) ?? null,
    exchangeRate: finiteOrNull(asNullableNumber(record.exchangeRate)),
    amountPLN: finiteOrNull(asNullableNumber(record.amountPLN)),
    ...('splitMethod' in record ? { splitMethod: asOptionalString(record.splitMethod) ?? null } : {}),
  };
}

/**
 * Etap 2: narrow a full transport (as embedded in `PZDocument.sourceTransports`)
 * down to the trimmed picker shape. Lets a saved PZ render its own linked
 * transports through the same component as the supplier-order candidate list,
 * instead of a second, near-identical row renderer.
 *
 * `pzNumbers` is deliberately empty here: on a PZ document the "which PZs came
 * off this truck" question is already answered by the document being looked at.
 */
export function mapTransportSummaryToSupplierOrderTransport(
  transport: TransportRouteSummaryDto,
): SupplierOrderTransportDto {
  // The full transport DTO stores "no value" as '' for some fields and
  // undefined for others; the picker shape says null once, so an absent plate
  // or date renders a single honest "—" instead of an empty chip.
  const toNullableText = (value: string | null | undefined): string | null =>
    (typeof value === 'string' && value.trim() ? value : null);

  return {
    id: transport.id,
    routeNumber: transport.routeNumber,
    displayLabel: transport.displayLabel ?? null,
    transportCompany: transport.transportCompany,
    loadingDate: toNullableText(transport.loadingDate),
    departureDate: toNullableText(transport.departureDate),
    licensePlate1: toNullableText(transport.licensePlate1),
    vehicleRegistration: toNullableText(transport.vehicleRegistration),
    status: transport.status,
    pzNumbers: [],
  };
}

/**
 * Etap 2: `GET /api/supplier-orders/:id/transports` — the trimmed
 * "which trucks brought this ZD in" read that drives the PZ receipt picker.
 * Every optional field defaults to null/[] so a partially-populated transport
 * (no plate entered yet, no PZ issued yet) renders an honest "—" instead of
 * breaking the card.
 */
export function normalizeSupplierOrderTransports(
  raw: unknown,
  entity = 'supplierOrderTransports',
): SupplierOrderTransportDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }
    const kind = normalizeTransportKind(record?.kind);

    return {
      id: asString(record?.id),
      routeNumber: asString(record?.routeNumber),
      displayLabel: asOptionalString(record?.displayLabel) ?? null,
      transportCompany: asString(record?.transportCompany),
      loadingDate: asOptionalString(record?.loadingDate) ?? null,
      departureDate: asOptionalString(record?.departureDate) ?? null,
      // Both plate fields are optional in the response — an older backend
      // returns only `vehicleRegistration`, a newer one carries `licensePlate1`
      // as well. Absence of either stays null rather than ''.
      licensePlate1: asOptionalString(record?.licensePlate1) ?? null,
      vehicleRegistration: asOptionalString(record?.vehicleRegistration) ?? null,
      status: normalizeTransportStatus(record?.status, `${entity}[${index}].status`),
      pzNumbers: asArray(record?.pzNumbers, (pzNumber) => asString(pzNumber)).filter(Boolean),
      // Rodzaj auta: absent or unrecognised stays absent (unknown), never a guess.
      ...(kind ? { kind } : {}),
      ...(asRecord(record?.freight) ? { freight: normalizeSupplierOrderTruckFreight(record!.freight) } : {}),
    };
  }).filter((transport) => Boolean(transport.id));
}

function normalizeSupplierOrderTruckFreight(raw: unknown): SupplierOrderTruckFreightDto {
  const record = asRecord(raw);
  const number = (value: unknown) => finiteOrNull(asNullableNumber(value));
  return {
    currency: asOptionalString(record?.currency) ?? 'PLN',
    transportRate: number(record?.transportRate),
    percent: number(record?.percent),
    amountPLN: number(record?.amountPLN),
    exchangeRate: number(record?.exchangeRate),
    carriedM2: number(record?.carriedM2),
    perM2PLN: number(record?.perM2PLN),
    perM2: number(record?.perM2),
    allocationKey: asOptionalString(record?.allocationKey) ?? null,
    lines: asArray(record?.lines, (line) => {
      const row = asRecord(line);
      return {
        id: asString(row?.id),
        name: asString(row?.name),
        carriedM2: number(row?.carriedM2),
        amountPLN: number(row?.amountPLN),
        perM2PLN: number(row?.perM2PLN),
        perM2: number(row?.perM2),
      };
    }),
  };
}

function normalizeTransportProducts(raw: unknown, entity: string): TransportProductDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    return {
      id: asString(record?.id),
      cargoItemId: asOptionalString(record?.cargoItemId),
      orderId: asOptionalString(record?.orderId),
      name: asString(record?.name),
      quantity: record?.quantity == null ? undefined : asNumber(record?.quantity),
      quantityPcs: record?.quantityPcs == null ? null : asNumber(record.quantityPcs),
      quantityM2: record?.quantityM2 == null ? null : asNumber(record.quantityM2),
      quantityM3: record?.quantityM3 == null ? null : asNumber(record.quantityM3),
      unit: asOptionalString(record?.unit),
      deliveredQuantity: record?.deliveredQuantity == null ? null : asNumber(record.deliveredQuantity),
      deliveredQuantityPcs: record?.deliveredQuantityPcs == null
        ? null
        : asNumber(record.deliveredQuantityPcs),
      deliveredQuantityM2: record?.deliveredQuantityM2 == null
        ? null
        : asNumber(record.deliveredQuantityM2),
      deliveredQuantityM3: record?.deliveredQuantityM3 == null
        ? null
        : asNumber(record.deliveredQuantityM3),
      commercialQuantity: record?.commercialQuantity == null ? null : asNumber(record.commercialQuantity),
      commercialUnit: asOptionalString(record?.commercialUnit) ?? null,
      grossWeight: record?.grossWeight == null ? undefined : asNumber(record?.grossWeight),
      width: record?.width == null ? undefined : asNumber(record?.width),
      height: record?.height == null ? undefined : asNumber(record?.height),
      length: record?.length == null ? undefined : asNumber(record?.length),
      thickness: record?.thickness == null ? undefined : asNumber(record?.thickness),
      productCategoryDimensionDisplayProfile:
        asOptionalString(record?.productCategoryDimensionDisplayProfile),
      productVariantId: asOptionalString(record?.productVariantId) ?? null,
      catalogProductName: asOptionalString(record?.catalogProductName) ?? null,
      variantName: asOptionalString(record?.variantName) ?? null,
      variantAttributes: normalizeTransportVariantAttributes(record?.variantAttributes),
      variantIsDefault: record?.variantIsDefault == null
        ? undefined
        : asBoolean(record.variantIsDefault),
      catalogProductWidth: record?.catalogProductWidth == null
        ? undefined
        : asNumber(record.catalogProductWidth),
      catalogProductHeight: record?.catalogProductHeight == null
        ? undefined
        : asNumber(record.catalogProductHeight),
      catalogProductLength: record?.catalogProductLength == null
        ? undefined
        : asNumber(record.catalogProductLength),
      catalogProductThickness: record?.catalogProductThickness == null
        ? undefined
        : asNumber(record.catalogProductThickness),
      catalogProductGrossWeight: record?.catalogProductGrossWeight == null
        ? undefined
        : asNumber(record.catalogProductGrossWeight),
      ...(record?.countInPiecesOnly === true ? { countInPiecesOnly: true } : {}),
    };
  });
}

function normalizeTransportVariantAttributes(raw: unknown): Record<string, string> | undefined {
  const record = asRecord(raw);
  if (!record) return undefined;

  const entries = Object.entries(record).flatMap(([rawKey, rawValue]) => {
    const key = rawKey.trim();
    const value = (
      typeof rawValue === 'string'
      || typeof rawValue === 'number'
      || typeof rawValue === 'boolean'
    ) ? String(rawValue).trim() : '';
    return key && value ? [[key, value] as const] : [];
  });

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function normalizeTransportCargoItems(raw: unknown, entity: string): TransportCargoItemDto[] {
  return asArray(raw, (item, index) => {
    const record = asRecord(item);

    return {
      id: asString(record?.id),
      transportId: asString(record?.transportId),
      orderId: asString(record?.orderId),
      orderProductId: asOptionalString(record?.orderProductId),
      productVariantId: asOptionalString(record?.productVariantId) ?? null,
      supplierOrderItemId: asOptionalString(record?.supplierOrderItemId) ?? null,
      productName: asString(record?.productName),
      quantity: asNumber(record?.quantity),
      quantityPcs: record?.quantityPcs == null ? null : asNumber(record.quantityPcs),
      quantityM2: record?.quantityM2 == null ? null : asNumber(record.quantityM2),
      quantityM3: record?.quantityM3 == null ? null : asNumber(record.quantityM3),
      deliveredQuantity: record?.deliveredQuantity == null ? null : asNumber(record.deliveredQuantity),
      deliveredQuantityPcs: record?.deliveredQuantityPcs == null
        ? null
        : asNumber(record.deliveredQuantityPcs),
      deliveredQuantityM2: record?.deliveredQuantityM2 == null
        ? null
        : asNumber(record.deliveredQuantityM2),
      deliveredQuantityM3: record?.deliveredQuantityM3 == null
        ? null
        : asNumber(record.deliveredQuantityM3),
      deliveryConfirmedAt: asOptionalString(record?.deliveryConfirmedAt) ?? null,
      deliveryConfirmedById: asOptionalString(record?.deliveryConfirmedById) ?? null,
      unit: asOptionalString(record?.unit),
      commercialQuantity: record?.commercialQuantity == null ? null : asNumber(record.commercialQuantity),
      commercialUnit: asOptionalString(record?.commercialUnit) ?? null,
      goodsValueTotal: record?.goodsValueTotal == null ? null : asNumber(record?.goodsValueTotal),
      manualAllocatedTransportCost: record?.manualAllocatedTransportCost == null
        ? null
        : asNumber(record?.manualAllocatedTransportCost),
      width: record?.width == null ? undefined : asNumber(record?.width),
      height: record?.height == null ? undefined : asNumber(record?.height),
      length: record?.length == null ? undefined : asNumber(record?.length),
      thickness: record?.thickness == null ? undefined : asNumber(record?.thickness),
      dimensionDisplayProfile: asOptionalString(record?.productCategoryDimensionDisplayProfile) ?? null,
      catalogProductWidth: finiteOrNull(asNullableNumber(record?.catalogProductWidth)),
      catalogProductHeight: finiteOrNull(asNullableNumber(record?.catalogProductHeight)),
      catalogProductLength: finiteOrNull(asNullableNumber(record?.catalogProductLength)),
      catalogProductThickness: finiteOrNull(asNullableNumber(record?.catalogProductThickness)),
      ...(record?.freightPLN === undefined
        ? {}
        : { freightPLN: record.freightPLN === null ? null : asNumber(record.freightPLN) }),
      grossWeight: record?.grossWeight == null ? undefined : asNumber(record?.grossWeight),
      ...(record?.countInPiecesOnly === true ? { countInPiecesOnly: true } : {}),
    };
  });
}

function normalizeTransportCostAllocation(
  raw: unknown,
  entity: string,
): TransportCostAllocationResult | undefined {
  if (raw == null) {
    return undefined;
  }

  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    method: normalizeTransportCostAllocationMethod(record?.method),
    items: normalizeTransportCostAllocationItems(record?.items, `${entity}.items`),
    summary: {
      totalGoodsValue: asNumber(asRecord(record?.summary)?.totalGoodsValue),
      totalAllocatedTransportCost: asNumber(asRecord(record?.summary)?.totalAllocatedTransportCost),
      totalLandedCost: asNumber(asRecord(record?.summary)?.totalLandedCost),
      // null: the server cannot say the truck in PLN (backend utils/truckSharesPln.js).
      allocationDifference: asNullableNumber(asRecord(record?.summary)?.allocationDifference),
    },
    warnings: asArray(record?.warnings, (item, index) => {
      const warning = asRecord(item);
      const code = asString(warning?.code) as TransportCostAllocationWarningCode;
      // MISSING_DIMENSIONS is an aggregated warning carrying the offending
      // lines' itemId/name in warning.items[]; the detail page's renderer
      // shows them as a "Pozycje: <names>" suffix, same as the form. Dropping
      // this field silently emptied that suffix on TransportRouteDetail.
      const items = asOptionalArray(warning?.items, (warningItem) => {
        const warningItemRecord = asRecord(warningItem);
        return {
          itemId: asOptionalString(warningItemRecord?.itemId),
          name: asOptionalString(warningItemRecord?.name) ?? null,
        };
      });

      return {
        code,
        itemId: asOptionalString(warning?.itemId),
        ...(items ? { items } : {}),
      };
    }),
  };
}

function normalizeTransportCostAllocationItems(
  raw: unknown,
  entity: string,
): TransportCostAllocationResult['items'] {
  const rawItems = Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];

  return asArray(rawItems, (item, index) => {
    const record = asRecord(item);
    if (!record) {
      reportDtoAnomaly(`${entity}[${index}]`, 'root', item);
    }

    return {
      id: asOptionalString(record?.id) || asOptionalString(record?.itemId),
      quantity: asNumber(record?.quantity),
      unit: asOptionalString(record?.unit) ?? null,
      goodsValueTotal: asNumber(record?.goodsValueTotal),
      manualAllocatedTransportCost: asNumber(record?.manualAllocatedTransportCost),
      // Contract round 6: null = "nie da się rozdzielić" (not 0) — the
      // derived money fields below are null too.
      allocatedTransportCost: record?.allocatedTransportCost == null ? null : asNumber(record?.allocatedTransportCost),
      goodsCostPerUnit: record?.goodsCostPerUnit == null ? null : asNumber(record?.goodsCostPerUnit),
      transportCostPerUnit: record?.transportCostPerUnit == null ? null : asNumber(record?.transportCostPerUnit),
      landedCostTotal: record?.landedCostTotal == null ? null : asNumber(record?.landedCostTotal),
      landedCostPerUnit: record?.landedCostPerUnit == null ? null : asNumber(record?.landedCostPerUnit),
      // Share of the line in the truck's freight (4 dp); null = not known.
      allocationSharePercent: record?.allocationSharePercent == null ? null : asNumber(record?.allocationSharePercent),
      // Per-metric unit costs computed server-side — null stays null (metric
      // not computable for this line), never coerced to 0.
      transportCostPerAreaSqm: record?.transportCostPerAreaSqm == null ? null : asNumber(record?.transportCostPerAreaSqm),
      transportCostPerVolumeM3: record?.transportCostPerVolumeM3 == null ? null : asNumber(record?.transportCostPerVolumeM3),
    };
  });
}

function normalizeTransportCostAllocationMethod(value: unknown): TransportCostAllocationMethod {
  return TRANSPORT_COST_ALLOCATION_METHODS.includes(value as TransportCostAllocationMethod)
    ? value as TransportCostAllocationMethod
    : 'BY_COMMERCIAL_QTY';
}

function normalizeTransportStatus(value: unknown, entity: string): TransportRouteStatus {
  if (typeof value === 'string' && ROUTE_STATUSES.includes(value as TransportRouteStatus)) {
    return value as TransportRouteStatus;
  }

  reportDtoAnomaly(entity, 'value', value);
  return 'PLANNED';
}

function pickTransportCustomerName(
  record: ReturnType<typeof asRecord>,
  firstOrder: TransportOrderSummaryDto | undefined,
): string {
  if (!record) {
    return firstOrder?.customerName || '';
  }

  const rawCustomer = record.customer;

  if (typeof rawCustomer === 'string' && rawCustomer.trim()) {
    return rawCustomer;
  }

  return asOptionalString(record.customerName) || firstOrder?.customerName || '';
}
